import * as React from 'react';
import { toast } from 'sonner';
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowLeft01Icon, Tick02Icon } from '@hugeicons/core-free-icons';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { getTourEntry, getToursForContext, tourMatches } from '@/constants/tours/tours';
import { plural } from '@/lib/plural';
import { resetTours, useTourProgress } from '@/lib/tour-progress';
import { useAvailableTours } from './TourHost';
import useTourContext from './useTourContext';

/**
 * Help → Guided tours: the tours for the screen you're on first, then every tour grouped by
 * area, with a tick on finished ones. A screen tour that needs a record open (an agent, a
 * collection) says what to open instead of starting. `onStart(tourId)` closes the menu.
 */
export default function TourMenu({ onBack, onStart }) {
  const context = useTourContext();
  const tours = useAvailableTours();
  const { userKey, progress } = useTourProgress();
  const here = getToursForContext(tours, context);
  const hereIds = new Set(here.map(t => t.id));
  const doneCount = tours.filter(t => progress.tours[t.id] === 'completed').length;

  const groups = [];
  tours
    .filter(t => !hereIds.has(t.id))
    .forEach(t => {
      const group = groups.find(g => g.label === t.group);
      if (group) group.tours.push(t);
      else groups.push({ label: t.group, tours: [t] });
    });
  const sections = [...(here.length ? [{ label: 'On this screen', tours: here }] : []), ...groups];

  const restart = () => {
    resetTours(userKey);
    toast.success('Tours reset');
  };

  return (
    <div className="flex flex-col animate-in fade-in-0 duration-150 ease-out-quad">
      <div className="flex items-center gap-1 px-1 pt-0.5 pb-1.5">
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          aria-label="Back to Help"
          onClick={onBack}
          // The option that opened this view is gone, so keyboard focus lands here.
          autoFocus
          className="text-muted-foreground hover:text-foreground"
        >
          <HugeiconsIcon icon={ArrowLeft01Icon} strokeWidth={2} />
        </Button>
        <span className="text-sm font-medium">Guided tours</span>
        <span className="ms-auto pe-1.5 text-xs text-muted-foreground tabular-nums">
          {doneCount} of {plural(tours.length, 'tour')} done
        </span>
      </div>

      <div className="flex max-h-[min(26rem,60vh)] flex-col overflow-y-auto">
        {sections.map(section => (
          <section
            key={section.label}
            aria-labelledby={`tour-group-${section.label}`}
            className="flex flex-col"
          >
            <h3
              id={`tour-group-${section.label}`}
              className="px-2.5 pt-2.5 pb-1 text-xs font-medium text-muted-foreground"
            >
              {section.label}
            </h3>
            <ul className="flex flex-col gap-0.5">
              {section.tours.map(tour => {
                const done = progress.tours[tour.id] === 'completed';
                // A screen tour runs here when it matches, or opens its entry page from
                // elsewhere; on its own page at another step it would show the wrong screen.
                const entry = getTourEntry(tour);
                const available =
                  tourMatches(tour, context) ||
                  (!tour.match ? Boolean(entry) : Boolean(entry) && entry !== context.pathname);
                const needsId = `tour-needs-${tour.id}`;
                return (
                  <li key={tour.id}>
                    <button
                      type="button"
                      aria-disabled={!available || undefined}
                      aria-describedby={available ? undefined : needsId}
                      onClick={() => available && onStart(tour.id)}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-start transition-colors duration-150 ease-out-quad outline-none focus-visible:bg-foreground/[0.06] focus-visible:ring-2 focus-visible:ring-ring/50',
                        available ? 'hover:bg-foreground/[0.06]' : 'cursor-not-allowed opacity-50'
                      )}
                    >
                      <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                        <HugeiconsIcon icon={tour.icon} strokeWidth={2} className="size-4" />
                      </span>
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-sm font-medium text-foreground">
                          {tour.title}
                        </span>
                        <span
                          id={available ? undefined : needsId}
                          className="truncate text-[13px] text-muted-foreground"
                        >
                          {available ? tour.description : tour.needs}
                        </span>
                      </span>
                      {done ? (
                        <HugeiconsIcon
                          icon={Tick02Icon}
                          strokeWidth={2}
                          className="size-4 shrink-0 text-success"
                          aria-label="Done"
                        />
                      ) : (
                        <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                          {plural(tour.steps.length, 'step')}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>

      <div className="mt-1 border-t px-1 pt-1.5">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="w-full justify-start text-muted-foreground hover:text-foreground"
          onClick={restart}
        >
          Show tours to me again
        </Button>
      </div>
    </div>
  );
}
